// /police-clearance-foreigner/$nationality — TH per-nationality page
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES, getForeignerNationality } from "@/data/police-foreigner-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { ShieldCheck, MessageCircle, Phone, BadgeCheck, AlertTriangle, ListChecks, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/police-clearance-foreigner/$nationality/")({
  loader: ({ params }) => {
    if (!getForeignerNationality(params.nationality)) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getForeignerNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Not found", "/police-clearance-foreigner/$nationality", "th")}] };
    const url = `${SITE}/police-clearance-foreigner/${n.slug}`;
    const title = `${n.homeAuthority} สำหรับชาว${n.nameTh}ในไทย เริ่ม ฿${n.priceFromTh.toLocaleString()} | NYC`;
    const desc = `${n.oneLineTh} · ${n.timelineDays} · โดยทนายสภาทนายความ — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance-foreigner/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/police-clearance-foreigner/$nationality", "th")},
        { property: "og:title", content: ctaTitle(title, "/police-clearance-foreigner/$nationality", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance-foreigner/$nationality", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
        { property: "og:type", content: "article" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance-foreigner/${n.slug}`, `/en/police-clearance-foreigner/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${n.homeAuthority} สำหรับชาว${n.nameTh}ในไทย`,
              serviceType: "Foreign Police Clearance Procurement",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/police-clearance` },
                { "@type": "ListItem", position: 3, name: "สำหรับชาวต่างชาติในไทย", item: `${SITE}/police-clearance-foreigner` },
                { "@type": "ListItem", position: 4, name: `ชาว${n.nameTh}`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: NationalityPage,
});

function NationalityPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getForeignerNationality(slug)!;
  const related = FOREIGNER_NATIONALITIES.filter((x) => x.slug !== slug).slice(0, 8);
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance-foreigner" className="hover:text-gold">ชาวต่างชาติในไทย</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">ชาว{n.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {n.countryTh} · {n.homeAuthority}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            ขอ {n.homeAuthority} สำหรับชาว{n.nameTh}ในไทย
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ประสานงานผ่านหน่วยงานบ้านเกิด ({n.homeAuthorityEn}) + {n.hague ? "Apostille ประเทศต้นทาง" : "Embassy Legalization"} + แปลไทยรับรอง MFA — เริ่ม ฿{n.priceFromTh.toLocaleString()} · {n.timelineDays}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {n.oneLineTh}
        </blockquote>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ใช้ทำอะไรได้บ้าง</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {n.commonUseCases.map((u, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">✓ {u}</div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <ListChecks className="h-6 w-6 text-amber-600" /> ขั้นตอนครบวงจร
          </h2>
          <ol className="mt-5 space-y-3 text-[15px]">
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">1. ยื่นเอกสาร:</strong> Passport + ที่อยู่ในไทย + แบบฟอร์มมอบอำนาจ (ถ้าจำเป็น)</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">2. ประสาน {n.homeAuthorityEn}:</strong> NYC ยื่นคำขอผ่านช่องทาง Official ของ {n.countryEn}</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">3. รับใบ PC จากประเทศต้นทาง:</strong> รับส่งกลับไทยผ่าน courier tracking</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">4. {n.hague ? "Apostille" : "Legalization"}:</strong> {n.hague ? "ปิด Apostille ที่หน่วยงานต้นทาง" : `Notary → Foreign Ministry ${n.countryEn} → ${n.embassyBkkEn}`}</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">5. แปลไทย + รับรอง MFA:</strong> แปลโดยนักแปลขึ้นทะเบียนกรมการกงสุล + รับรองที่กรมการกงสุล ถ.แจ้งวัฒนะ</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">6. จัดส่ง:</strong> รับที่สาขา NYC หรือ courier ถึงบ้าน</li>
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <AlertTriangle className="h-6 w-6 text-rose-600" /> ข้อพลาดที่พบบ่อย
          </h2>
          <ul className="mt-5 space-y-2">
            {n.pitfalls.map((p, i) => (
              <li key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4 text-[14px] text-slate-800">⚠️ {p}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `ต้องบินกลับ ${n.countryTh} ไหม?`, a: `ไม่จำเป็น — NYC ประสานผ่าน ${n.homeAuthorityEn} หรือ ${n.embassyBkkTh} ให้ได้ในหลายกรณี` },
                { q: `ใช้เวลารวมกี่วัน?`, a: `${n.timelineDays} (รวมทุกขั้นตอน)` },
                { q: `ราคารวมทุกอย่างเท่าไร?`, a: `เริ่ม ฿${n.priceFromTh.toLocaleString()} — รวมค่าธรรมเนียม ${n.homeAuthorityEn}, ${n.hague ? "Apostille" : "Legalization"}, แปลไทยรับรอง MFA` },
                { q: `สถานทูต${n.countryTh}อยู่ที่ไหน?`, a: n.embassyBkkTh },
                { q: `ต้องใช้ Fingerprint หรือไม่?`, a: n.iso === "US" || n.iso === "CA" || n.iso === "SG" ? "จำเป็น — NYC ประสานทำที่สถานีตำรวจไทย + รับรองตราประทับ" : "ส่วนใหญ่ใช้ Name Check เพียงพอ — บางกรณีต้อง Fingerprint (แจ้งตอนประเมิน)" },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="h-6 w-6 text-amber-600" /> สัญชาติอื่นๆ
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/police-clearance-foreigner/$nationality" params={{ nationality: r.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">ชาว{r.nameTh}</span>
                <span className="ml-auto text-[10px] text-slate-500">฿{r.priceFromTh.toLocaleString()}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  );
}
